import { Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { FaqListItemAnswer } from '@apple/features/faq/components/FaqListItemAnswer';
import type { Faq, FaqFilters } from '@apple/features/faq/management.models';
import { faqManagementListQueryOptions } from '@apple/features/faq/management.queries';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/content/faq')({
	wrapInSuspense: true,
	component: FAQManagePageRoute,
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ContentManagement',
		});
	},
});

const defaultFilters: FaqFilters = {};
const defaultSorting: SortingState = [{ id: 'rank', desc: false }];

function FAQManagePageRoute() {
	const { t } = useTranslation('content');
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const faqQuery = useQuery(faqManagementListQueryOptions);

	const tableState = useTableState<Faq, FaqFilters>({
		search,
		navigate,
		defaultFilters: defaultFilters,
		defaultSorting: defaultSorting,
		fieldMap: [],
	});

	const table = useTable({
		features,
		data: faqQuery.data,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		columnResizeMode: 'onChange',

		columns: [
			{
				accessorKey: 'question',
				header: t('faqs.headers.question'),
				filter: {
					group: t('projects.filter-groups.details.title'),
				},
				size: 180,
				cell: ({ row }) => (
					<Link
						to='/content/faq/$faqId'
						state={{ from: location }}
						children={row.original.question}
						params={{ faqId: row.original.id! }}
						underline='never'
					/>
				),
			},
			{
				accessorKey: 'answer',
				header: t('faqs.headers.answer'),
				size: 300,
				filter: {
					group: t('projects.filter-groups.details.title'),
				},
				cell: ({ row }) => <FaqListItemAnswer answer={row.original.answer} />,
			},
			{
				accessorKey: 'active',
				header: t('faqs.headers.status'),
				size: 50,
				filter: {
					label: t('faqs.labels.active'),
					group: t('projects.filter-groups.details.title'),
					variant: 'switch',
					getFilterDisplayValue: value => {
						return value === true ? t('faqs.labels.active') : t('faqs.labels.inactive');
					},
				},
				cell: ({ row }) => (
					<Text>
						{row.original.active ? t('faqs.labels.active') : t('faqs.labels.inactive')}
					</Text>
				),
			},
			{
				size: 50,
				accessorKey: 'rank',
				header: t('faqs.headers.rank'),
				filter: {
					group: t('projects.filter-groups.details.title'),
					variant: 'number-range-slider',
				},
			},
		],
	});

	return (
		<TableLayout
			table={table}
			title={t('faqs.title')}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('faqs.add.title')}
					icon={AddIcon}
					onClick={() =>
						void navigate({
							to: '/content/faq/add',
							state: { from: location },
							viewTransition: false,
						})
					}
				/>,
			]}
		>
			<DataTable table={table} loading={faqQuery.isFetching} />
		</TableLayout>
	);
}
